import React, { MutableRefObject } from 'react';
import styled, { css } from 'styled-components';
import Button from '../Button/Button';
import { Size } from '../types';
import { DropdownContentButtonStyling } from './CommonStyling';
import { ComponentLStyling, ComponentXLStyling, ComponentMStyling, ComponentSStyling, ComponentTextStyle } from '../styles/typography';
import { COLORS, focusStyles, scrollBarStyling } from '../styles';
import { Checkbox, RadioButton } from '../InputFields';
import { Z_INDEXES } from '../styles/z-indexes';
import MenuItem from '../MenuItem/MenuItem';
import { defaultOnMouseDownHandler } from '../common';
import ReactDOM from 'react-dom';

const MAX_MENU_HEIGHT = 240;
const AVG_OPTION_HEIGHT = 48;
const OFFSET_BEFORE_SHOW = 1000000;

export const DropdownContentContainer = styled.div<{ offsetTop: string; offsetLeft: string; isButton: boolean; alignLeft?: boolean; size?: Size; scrollable?: boolean; maxHeight?: string }>`
  display: none;
  pointer-events: auto;
  position: absolute;
  background-color: ${COLORS.white};

  z-index: ${Z_INDEXES.dropdown};
  margin: ${(props) => (props.isButton ? '-4px' : '4px 0px')};

  &.show {
    display: flex;
    flex-direction: column;
    justify-content: center;
    &.down,
    &.up {
      ${(props) => `transform: translate(${props.offsetLeft}, ${props.offsetTop});`}
    }
  }

  transform: translate(-${OFFSET_BEFORE_SHOW}px, -${OFFSET_BEFORE_SHOW}px);

  &.outline:focus:after {
    content: '';
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    outline: 4px solid black;
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:focus {
    outline: none;
  }

  ${(props) => (props.isButton ? DropdownContentButtonStyling : '')};

  border: 1px solid ${COLORS.neutral_100};
  box-sizing: border-box;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  padding: 4px 0px;

  min-width: ${(props) => (props.size == Size.Large ? '320px' : props.size == Size.Medium ? '280px' : '240px')};
  border-radius: ${(props) => (props.size == Size.Large ? '10px' : props.size == Size.Medium ? '8px' : '6px')};

  max-height: ${(props) => (!props.scrollable ? 'auto' : props.maxHeight ? props.maxHeight : props.size == Size.Large ? '320px' : props.size == Size.Medium ? '280px' : '240px')};
`;

export const DropdownContentItemsContainer = styled.div<{ size?: Size }>`
  > *:hover {
    z-index: ${Z_INDEXES.hover};
  }

  > *:active,
  > *.active {
    z-index: ${Z_INDEXES.active};
  }

  > *:focus {
    z-index: ${Z_INDEXES.focus};
  }
`;

export const DropdownContentListContainer = styled.div<{ scrollable?: boolean; size?: Size; itemsType?: string; outline?: boolean }>`
  ${(props) => (props.scrollable ? 'overflow-y: scroll;' : '')}
  ${(props) => (props.scrollable ? 'margin-right: 6px;' : '')}
  ${(props) => (props.scrollable ? 'padding-right: 6px;' : '')}
  ${(props) => (props.itemsType != 'normal' ? 'margin-left: 4px;' : '')}
  div.divider {
    display: block;
    width: calc(100% - 40px);
    margin-left: 16px;
    border-top: 1px solid #e5e5e5;
    position: absolute;
    top: 0px;
  }

  ${DropdownContentItemsContainer} {
    padding-top: ${(props) => (props.size == Size.Large ? '8px' : props.size == Size.Medium ? '4px' : '0px')};
    padding-bottom: ${(props) => (props.size == Size.Large ? '8px' : props.size == Size.Medium ? '4px' : '0px')};

    word-break: break-word;
  }

  *:focus {
    outline: none !important;
  }

  ${scrollBarStyling(Size.Small)}
`;

const DropdownContentMenuContentContainer = styled.div<{ size?: Size }>`
  h1,
  h2,
  h3 {
    margin-block-start: 0px;
    margin-block-end: 0px;
    ${(props) => props.size == Size.Large && ComponentXLStyling(ComponentTextStyle.Bold, COLORS.neutral_500)}
    ${(props) => props.size == Size.Medium && ComponentLStyling(ComponentTextStyle.Bold, COLORS.neutral_500)}
    ${(props) => (props.size == Size.Small || !props.size) && ComponentMStyling(ComponentTextStyle.Bold, COLORS.neutral_500)}
    padding: ${(props) => (props.size == Size.Small || !props.size ? '10px 16px 6px' : props.size == Size.Medium ? '16px 24px 8px' : '16px 32px 8px')};
  }

  p {
    margin-block-start: 0px;
    margin-block-end: 0px;
    ${(props) => props.size == Size.Large && ComponentLStyling(ComponentTextStyle.Regular, COLORS.neutral_500)}
    ${(props) => props.size == Size.Medium && ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_500)}
    ${(props) => (props.size == Size.Small || !props.size) && ComponentSStyling(ComponentTextStyle.Regular, COLORS.neutral_500)}
    padding: ${(props) => (props.size == Size.Small || !props.size ? '8px 16px' : props.size == Size.Medium ? '12px 24px' : '16px 32px')};
  }
`;

const DividerContainer = styled.div<{ size?: Size }>`
  position: relative;

  margin-top: ${(props) => (props.size == Size.Large ? '16px' : props.size == Size.Medium ? '12px' : props.size == Size.Small || !props.size ? '8px' : '6px')};

  div.divider {
    width: 100%;
    margin: 0px;
  }
`;

const DropdownContentTopItemContainer = styled.div<{ size?: Size }>`
  border-bottom-style: solid;
  border-bottom-color: ${COLORS.neutral_200};
  border-bottom-width: 1px;
  margin-right: 22px;
`;

const DropdownContentActionButtonContainer = styled.div<{ size?: Size; scrollable?: boolean }>`
  ${(props) => (props.scrollable ? `border-top: 1px solid  ${COLORS.neutral_200};` : '')}
  ${(props) => (props.scrollable ? 'margin-top: 4px;' : '')}

  padding: ${(props) => (props.size === Size.Small || !props.size ? '4px 16px 0px' : props.size === Size.Medium ? '12px 16px 8px' : '16px 16px 12px')};

  button {
    width: 100%;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: ${Z_INDEXES.modal};
`;

export interface DropdownItem {
  value: string;
  displayLabel?: string;
  noteLabel?: string;
  suggestion?: boolean;
  showDividerAbove?: boolean;
  disabled?: boolean;
  locked?: boolean;
  icon?: React.ReactNode;
  customContent?: React.ReactNode;
}

export interface DropdownCustomizationProps {
  itemsType?: 'radio' | 'checkbox' | 'normal';
  menuContent?: React.ReactNode;
  scrollable?: boolean;
  multiSelect?: boolean;
  maxHeight?: string;
  actionLabel?: string;
  actionVariant?: 'primary' | 'secondary' | 'tertiary' | undefined;
  actionIcon?: React.ReactNode;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  action: () => boolean | void | undefined;
  pinTopItem?: boolean;
  onValueUpdate: (values: string[]) => boolean | void | undefined;
  items: DropdownItem[];
}

export interface DropdownProps {
  isButton: boolean;
  customizationProps: DropdownCustomizationProps;
  alignLeft?: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  size?: Size;
  id: string;
  filter: string;
  width?: string;
  onActiveDescendantChanged?: (descendantId?: string) => void;
  selectedValues: string[];
  setSelectedValues: (val: string[]) => void;
  messageOnNoResults: string;
  focused: number | null;
  setFocused: (focused: number | null) => void;
  outline?: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  ariaRolesType?: 'input' | 'menu';
}

const DropdownContent = React.forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      id,
      customizationProps,
      alignLeft,
      isButton,
      size,
      isOpen,
      width,
      setIsOpen,
      filter,
      focused,
      setFocused,
      selectedValues,
      setSelectedValues,
      messageOnNoResults,
      outline,
      containerRef,
      onActiveDescendantChanged,
      ariaRolesType = 'menu',
    },
    ref,
  ) => {
    const [isUp, setIsUp] = React.useState<boolean>(false);
    const [locationDefined, setLocationDefined] = React.useState<boolean>(false);
    const [offset, setOffset] = React.useState<{ top: string; left: string }>({ top: '0', left: '0' });

    const dropdownContentRef = (ref as MutableRefObject<HTMLDivElement>) ?? React.useRef<HTMLDivElement>(null);

    if (!customizationProps.itemsType) customizationProps.itemsType = 'normal';
    if (!size) size = Size.Small;

    const itemsListRef = React.useRef<HTMLDivElement>(null);
    const actionButtonRef = React.useRef<HTMLButtonElement>(null);

    const determineDropUp = () => {
      const options = customizationProps.items;

      const rect = containerRef.current!.getBoundingClientRect();

      const windowHeight = window.innerHeight;
      const menuHeight = Math.min(MAX_MENU_HEIGHT, options.length * AVG_OPTION_HEIGHT);
      const instOffsetWithMenu = rect.top + rect.height + menuHeight;

      const up = instOffsetWithMenu >= windowHeight;

      setOffset({
        top: up ? `calc(-100% - ${rect.height}px ${isButton ? '' : '- 8px'})` : '0',
        left: alignLeft ? `calc(-${rect.width}px)` : `calc(-100%)`,
      });

      setIsUp(up);
      setLocationDefined(true);
    };

    const setNewFocusedElement = (index: number) => {
      if (outline) dropdownContentRef.current?.focus();

      const newFocusedElement = elRefs[index];
      if (onActiveDescendantChanged) onActiveDescendantChanged(index == 0 ? `${id}_topitem` : `${id}_${index}`);

      const oldFocusedElement = focused || focused === 0 ? elRefs[focused] : null;
      if (newFocusedElement && newFocusedElement !== oldFocusedElement) {
        setFocused(index);
        oldFocusedElement?.current?.classList.remove('dropdown-hover');

        newFocusedElement.current?.classList.add('dropdown-hover');
        newFocusedElement.current?.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        });
      }
    };

    const getFilteredItems = () => {
      const filtrationString = (filter ?? '').toUpperCase();
      return customizationProps.items.filter((x) => x?.value?.toUpperCase().includes(filtrationString) || x?.displayLabel?.toUpperCase().includes(filtrationString));
    };

    const findNextActiveIndex = (index: number, direction: number, items: DropdownItem[]) => {
      for (let i = index + direction; i > 0 && i <= items.length; i += direction) {
        if (!items[i - 1]?.disabled) {
          return i;
        }
      }

      return index;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isOpen) {
        if (e.key === 'ArrowUp' || e.key === 'Up') {
          e.preventDefault();
          let focusedNow = focused || focused === 0 ? focused : arrLength + 1;
          if (!!focusedNow) {
            focusedNow = focusedNow === 1 && haveTopItem() ? 0 : findNextActiveIndex(focusedNow, -1, getFilteredItems());

            setNewFocusedElement(focusedNow);
          }
        } else if (e.key === 'ArrowDown' || e.key === 'Down') {
          e.preventDefault();
          let focusedNow = focused;

          focusedNow = !!focusedNow || focusedNow === 0 ? findNextActiveIndex(focusedNow, 1, getFilteredItems()) : haveTopItem() ? 0 : 1;

          setNewFocusedElement(focusedNow);
        } else if (e.key === 'Escape' || e.key === 'Esc') {
          setIsOpen(false);
          setFocused(null);
          if (onActiveDescendantChanged) onActiveDescendantChanged(undefined);
        } else if ((e.key === 'Enter' || e.key === ' ') && focused !== null && e.target !== actionButtonRef?.current) {
          e.preventDefault();
          const focusedElement = elRefs[focused];
          if (focusedElement) {
            focusedElement.current?.click();
          }
          if (onActiveDescendantChanged) onActiveDescendantChanged(undefined);
        }
      }
    };

    React.useEffect(() => {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
  }, []);

    const scrollPosition = React.useRef<number | undefined>(0);
    const handleScroll = () => {
      scrollPosition.current = itemsListRef.current?.scrollTop;
    };
    React.useLayoutEffect(() => {
      if (itemsListRef.current) itemsListRef.current.scrollTop = scrollPosition.current ?? 0;
    }, [selectedValues]);

    const getCorrectRef = (ref: React.RefObject<HTMLElement>): React.RefObject<HTMLElement> => {
      switch (customizationProps.itemsType) {
        case 'normal':
          return (ref as React.RefObject<HTMLButtonElement>) || React.createRef<HTMLButtonElement>();
        default:
          return (ref as React.RefObject<HTMLDivElement>) || React.createRef<HTMLDivElement>();
      }
    };

    const arrLength = getFilteredItems().length;
    const [elRefs, setElRefs] = React.useState<React.RefObject<HTMLElement>[]>([]);

    React.useEffect(() => {
      if (elRefs?.length !== arrLength) {
        setFocused(null);
      }

      setElRefs((elRefs) =>
        Array(arrLength + 1)
          .fill(null)
          .map((_, i) => getCorrectRef(elRefs[i])),
      );
    }, [isOpen, arrLength]);

    React.useLayoutEffect(() => {
      isOpen && !locationDefined && determineDropUp();
    }, [isOpen, locationDefined]);

    const haveTopItem = () => {
      if (!customizationProps.pinTopItem) return false;

      if (customizationProps.itemsType === 'radio' && selectedValues && selectedValues?.length > 0) return true;
      else if (customizationProps.itemsType === 'checkbox' && (customizationProps.multiSelect || selectedValues?.length > 0)) return true;
      else if (customizationProps.itemsType === 'normal' && getSuggestions()?.length > 0) return true;
      return false;
    };

    const getSuggestions = () => {
      return getFilteredItems().filter((x) => x.suggestion);
    };

    /**
     * when dropdown opens set correct position of focused item
     * */
    React.useLayoutEffect(() => {
      if (isOpen && (focused || focused === 0)) {
        setNewFocusedElement(focused === 0 && !haveTopItem() ? 1 : focused);
      }
    }, [isOpen, focused]);

    /**
     * Reset dropdown content state on close
     * */
    React.useEffect(() => {
      if (!isOpen) {
        setLocationDefined(false);
        for (let elRef of elRefs) {
          elRef.current?.classList.remove('dropdown-hover');
        }
        setFocused(null);
        if(onActiveDescendantChanged) onActiveDescendantChanged(undefined);
        dropdownContentRef.current?.classList.remove('outline');
        dropdownContentRef.current?.blur();
      } else {
        if (outline) {
          dropdownContentRef.current?.classList.add('outline');
          dropdownContentRef.current?.focus();
        }
      }
    }, [isOpen, dropdownContentRef, focused, outline]);

    /**
     * Scroll item container to top when dropdown opens
     * */
    React.useEffect(() => {
      if (isOpen && itemsListRef.current) {
        itemsListRef.current.scrollTop = 0;
      }
    }, [isOpen]);

    React.useEffect(() => {
      if (isOpen) {
        const observer = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) {
            setIsOpen(false);
            //@ts-ignore
            document?.activeElement?.blur();
          }
        }, {threshold: [0], rootMargin: '8px'});

        observer.observe(containerRef.current!);

        calculateOffset();
        window.addEventListener('scroll', calculateOffset, true);
        window.addEventListener('resize', calculateOffset);

        return () => {
          observer.disconnect();
          window.removeEventListener('scroll', calculateOffset, true);
          window.removeEventListener('resize', calculateOffset);
        }
      }
      return () => {};
    }, [isOpen]);

    const handleItemClick = (selected: boolean, item: DropdownItem) => {
      let newValue: string[] = [];
      if (customizationProps.multiSelect) {
        if (selected) newValue = [...selectedValues, item.value];
        else newValue = selectedValues.filter((x) => x != item.value);
      } else newValue = selected ? [item.value] : [];

      setSelectedValues(newValue);
      if (customizationProps.onValueUpdate && customizationProps.onValueUpdate(newValue)) {
        setIsOpen(false);
      }
    };

    const getTopItem = () => {
      if (!customizationProps.pinTopItem) return;
      const visibleItems = getFilteredItems();
      const allSelected = selectedValues?.filter((x) => visibleItems.some((y) => y.value == x)).length == visibleItems.length;
      let showTopItem = false;
      const suggestions = getSuggestions();
      const displayLabel = selectedValues[0] ? customizationProps.items?.find((c) => c.value === selectedValues[0])?.displayLabel : undefined;
      if (customizationProps.itemsType == 'radio' && selectedValues && selectedValues?.length > 0) showTopItem = true;
      else if (customizationProps.itemsType == 'checkbox' && (customizationProps.multiSelect || selectedValues?.length > 0)) showTopItem = true;
      else if (customizationProps.itemsType == 'normal' && suggestions.length > 0) showTopItem = true;

      if (!showTopItem) return;

      const selectedFirst = selectedValues.length > 0 ? visibleItems.find((x) => x.value == selectedValues[0]) : null;

      return (
        <DropdownContentTopItemContainer size={size}>
          {customizationProps.itemsType === 'radio' && (
            <RadioButton
              ref={elRefs[0] as React.RefObject<HTMLDivElement>}
              role='menuitemradio'
              iconPointerEventsTransparent={true}
              onFocus={(e) => onActiveDescendantChanged && onActiveDescendantChanged(`${id}_topitem`)}
              tabIndexVal={-1}
              className={`${focused === 0 ? 'dropdown-hover' : ''}`}
              select={() => {}}
              label={selectedFirst?.displayLabel ?? selectedFirst?.value}
              size={size}
              id={`${id}_topitem`}
              selected={true}
            />
          )}
          {customizationProps.itemsType === 'checkbox' && (
            <Checkbox
              ref={elRefs[0] as React.RefObject<HTMLDivElement>}
              role='menuitemcheckbox'
              tabIndexVal={-1}
              className={`${focused === 0 ? 'dropdown-hover' : ''}`}
              select={(selected: boolean) => {
                if (!customizationProps.multiSelect) return;

                const newValues = selected ? visibleItems.map((x) => x.value) : [];
                setSelectedValues(newValues);
                if (customizationProps.onValueUpdate && customizationProps.onValueUpdate(newValues)) setIsOpen(false);
              }}
              onFocus={(e) => onActiveDescendantChanged && onActiveDescendantChanged(`${id}_topitem`)}
              iconPointerEventsTransparent={true}
              semiSelected={customizationProps.multiSelect && !allSelected && selectedValues?.length > 0}
              size={size}
              id={`${id}_checkbox_selectall`}
              label={customizationProps.multiSelect ? 'Select all' : selectedFirst?.displayLabel ?? selectedFirst?.value}
              selected={!customizationProps.multiSelect || allSelected}
            />
          )}
          {customizationProps.itemsType === 'normal' && (
            <>
              {suggestions.map((x) => (
                <MenuItem
                  role={ariaRolesType == 'input' ? 'option' : 'menu'} 
                  key={`${id}_topitem_${x.value}`}
                  item={x}
                  size={size}
                  active={false}
                  tabIndex={-1}
                  onFocus={(e) => onActiveDescendantChanged && onActiveDescendantChanged(`${id}_topitem`)}
                  className={`${focused === 0 ? 'dropdown-hover' : ''}`}
                  ref={elRefs[0] as React.RefObject<HTMLButtonElement>}
                  id={`${id}_topitem`}
                  onClickHandler={(e: any) => {
                    e.stopPropagation();
                    customizationProps.onValueUpdate([x.value]);
                    setSelectedValues([x.value]);
                    setIsOpen(false);
                    setFocused(null);
                  }}
                />
              ))}
            </>
          )}
        </DropdownContentTopItemContainer>
      );
    };

    const getElements = (messageOnNoResults: string, size: Size) => {
      if (getFilteredItems().length === 0) {
        return <MenuItem item={{ value: messageOnNoResults, disabled: true }} size={size} id="noResultsFound" active={false} onClickHandler={(e) => {}} />;
      }
      return (
        <DropdownContentItemsContainer size={size}>
          {getFilteredItems()
            .filter((x) => x && (customizationProps.itemsType != 'normal' || !x.suggestion))
            .map((item, index) => {
              return (
                <React.Fragment key={`key_${id}_${item.value}`}>
                  {item.customContent}
                  {!item.customContent && customizationProps.itemsType == 'radio' && (
                    <RadioButton
                      role='menuitemradio'
                      select={(selected: boolean) => handleItemClick(selected, item)}
                      ref={elRefs[index + 1] as React.RefObject<HTMLDivElement>}
                      size={size}
                      tabIndexVal={-1}
                      className={`${index + 1 === focused ? 'dropdown-hover' : ''}`}
                      iconPointerEventsTransparent={true}
                      disabled={item?.disabled}
                      key={`key_${id}_${index + 1}`}
                      onFocus={(e) => onActiveDescendantChanged && onActiveDescendantChanged(`${id}_${index + 1}`)}
                      id={`${id}_${index + 1}`}
                      label={item.displayLabel ?? item.value}
                      selected={selectedValues?.includes(item.value)}
                    />
                  )}
                  {!item.customContent && customizationProps.itemsType == 'checkbox' && (
                    <Checkbox
                      role='menuitemcheckbox'
                      select={(selected: boolean) => handleItemClick(selected, item)}
                      iconPointerEventsTransparent={true}
                      disabled={item?.disabled}
                      className={`${index + 1 === focused ? 'dropdown-hover' : ''}`}
                      ref={elRefs[index + 1] as React.RefObject<HTMLDivElement>}
                      size={size}
                      tabIndexVal={-1}
                      key={`key_${id}_${index + 1}`}
                      onFocus={(e) => onActiveDescendantChanged && onActiveDescendantChanged(`${id}_${index + 1}`)}
                      id={`${id}_${index + 1}`}
                      label={item.displayLabel ?? item.value}
                      selected={selectedValues?.includes(item.value)}
                    />
                  )}
                  {!item.customContent && (customizationProps.itemsType == 'normal' || !customizationProps.itemsType) && (
                    <MenuItem
                      role={ariaRolesType == 'input' ? 'option' : 'menu'} 
                      key={`key_${id}_${index + 1}`}
                      id={`${id}_${index + 1}`}
                      item={item}
                      tabIndex={-1}
                      className={`${index + 1 === focused ? 'dropdown-hover' : ''}`}
                      size={size}
                      onFocus={(e) => onActiveDescendantChanged && onActiveDescendantChanged(`${id}_${index + 1}`)}
                      active={selectedValues?.includes(item.value)}
                      ref={elRefs[index + 1] as React.RefObject<HTMLButtonElement>}
                      onClickHandler={(e: any) => {
                        e.stopPropagation();
                        customizationProps.onValueUpdate([item.value]);
                        setSelectedValues([item.value]);
                        setIsOpen(false);
                        setFocused(null);
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
        </DropdownContentItemsContainer>
      );
    };

    const calculateOffset = () => {
      const rect = containerRef?.current?.getBoundingClientRect();
      if (rect) {
        dropdownContentRef.current.style.left = `${rect.x + rect.width}px`;
        dropdownContentRef.current.style.top = `${rect.y + rect.height}px`;
        dropdownContentRef.current.style.width = width ?? `${rect.width}px`;
      }
      determineDropUp();
    };

    const cls = `${isOpen ? 'show' : ''} ${isOpen && outline ? 'outline' : ''} ${locationDefined ? (isUp ? 'up' : 'down') : ''}`;

    return (
      <Overlay>
        <DropdownContentContainer
          role={ariaRolesType == "menu" ? "menu" : "optionlist"}
          ref={dropdownContentRef}
          size={size}
          offsetTop={offset.top}
          id={id}
          offsetLeft={offset.left}
          tabIndex={-1}
          isButton={isButton}
          alignLeft={alignLeft}
          scrollable={customizationProps.scrollable}
          maxHeight={customizationProps.maxHeight}
          className={cls}>
          <DropdownContentListContainer
            role="group"
            size={size}
            itemsType={customizationProps.itemsType}
            onScroll={handleScroll}
            ref={itemsListRef}
            outline={outline}
            scrollable={customizationProps.scrollable}>
            {customizationProps.menuContent && (
              <DropdownContentMenuContentContainer size={size}>
                {customizationProps.menuContent}
                <DividerContainer size={size}>
                  <div className="divider" />
                </DividerContainer>
              </DropdownContentMenuContentContainer>
            )}
            {customizationProps.pinTopItem && getTopItem()}
            {getElements(messageOnNoResults, size)}
          </DropdownContentListContainer>
          {customizationProps.action && customizationProps.actionLabel && (
            <DropdownContentActionButtonContainer size={size} scrollable={customizationProps.scrollable}>
              <Button
                width="100%"
                role="menuitem"
                icon={customizationProps.actionIcon}
                ref={actionButtonRef}
                loading={customizationProps.actionLoading}
                disabled={customizationProps.actionDisabled}
                variant={customizationProps.actionVariant ?? 'primary'}
                size={size}
                onClick={() => customizationProps.action() && setIsOpen(false)}>
                {customizationProps.actionLabel}
              </Button>
            </DropdownContentActionButtonContainer>
          )}
        </DropdownContentContainer>
      </Overlay>
    );
  },
);

export default DropdownContent;
